import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  collection, 
  query, 
  orderBy, 
  limit, 
  onSnapshot, 
  where,
  getDocs,
  deleteDoc,
  doc,
  setDoc 
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import { formatLogEntry } from '../utils/logFormatters';
import LogFilters from '../components/logs/LogFilters';
import LogEntry from '../components/logs/LogEntry';
import LogViewer from '../components/logs/LogViewer';
import LogExporter from '../components/logs/LogExporter';
import PerformanceMetrics from '../components/logs/PerformanceMetrics';
import SecurityDashboard from '../components/logs/SecurityDashboard';
import SecurityAlerts from '../components/security/SecurityAlerts';
import ConsentManager from '../components/privacy/ConsentManager';
import CostMonitor from '../components/logs/CostMonitor';
import DarkModeToggle from '../components/ui/DarkModeToggle';
import { useTheme, getThemeStyles } from '../contexts/ThemeContext';
import { usePermissions, hasPermission, PERMISSIONS } from '../utils/permissions';
import useKeyboardShortcuts, { createLogsKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { useDebouncedSearch } from '../hooks/useDebounce';
import useVirtualScroll from '../hooks/useVirtualScroll';
import logCache from '../utils/logCache';
import { GDPR } from '../utils/dataPrivacy';
import { 
  FileText, 
  Download, 
  Search, 
  RefreshCw, 
  AlertCircle,
  Eye,
  Keyboard,
  Zap,
  Shield,
  Trash2,
  PlayCircle,
  CheckCircle
} from 'lucide-react';
import AuditLogger from '../services/auditLogger';

const LogsPage = () => {
  const [searchParams] = useSearchParams();
  
  // Safe theme usage with fallback
  let isDarkMode = false;
  let themeStyles = {};
  try {
    const theme = useTheme();
    isDarkMode = theme.isDarkMode;
    themeStyles = getThemeStyles(isDarkMode);
  } catch (error) {
    console.warn('ThemeProvider not available, using light theme');
    themeStyles = getThemeStyles(false);
  }
  
  const { userRole, permissions } = usePermissions();
  
  // Load filters from localStorage, then override with URL params
  const loadFiltersFromStorage = () => {
    try {
      const savedFilters = localStorage.getItem('auditLogFilters');
      const defaultFilters = {
        action: '',
        entityType: '',
        userId: '',
        severity: '',
        dateFrom: '',
        dateTo: ''
      };
      const filters = savedFilters ? JSON.parse(savedFilters) : defaultFilters;
      
      // Override with URL parameters if present
      const resourceId = searchParams.get('resourceId');
      const resource = searchParams.get('resource');
      const action = searchParams.get('action');
      const user = searchParams.get('user');
      
      if (resourceId) {
        // When viewing specific resource history, set search term to the ID
        setSearchTerm(resourceId);
      }
      if (resource) {
        filters.entityType = resource;
      }
      if (action) {
        filters.action = action;
      }
      if (user) {
        filters.userId = user;
      }
      
      return filters;
    } catch {
      return {
        action: '',
        entityType: '',
        userId: '',
        severity: '',
        dateFrom: '',
        dateTo: ''
      };
    }
  };

  // State management
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(loadFiltersFromStorage());
  const [expandedLogId, setExpandedLogId] = useState(null);
  const [showExporter, setShowExporter] = useState(false);
  const [showSecurityDashboard, setShowSecurityDashboard] = useState(false);
  const [showSecurityAlerts, setShowSecurityAlerts] = useState(false);
  const [showConsentManager, setShowConsentManager] = useState(false);
  const [viewMode, setViewMode] = useState('compact'); // 'compact' or 'detailed'
  const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState(null);
  
  // Cleanup and demo states
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  
  // Refs
  const searchInputRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const logContainerRef = useRef(null);

  // Debounced search
  const { 
    searchTerm, 
    setSearchTerm, 
    debouncedSearchTerm, 
    isSearching, 
    clearSearch 
  } = useDebouncedSearch('', 300);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [logsPerPage] = useState(50);

  // Real-time updates state
  const [isRealTime, setIsRealTime] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Clear all filters function (defined early for keyboard shortcuts)
  const clearFilters = useCallback(() => {
    setFilters({
      action: '',
      entityType: '',
      userId: '',
      severity: '',
      dateFrom: '',
      dateTo: ''
    });
    clearSearch();
  }, [clearSearch]);

  // Load logs from Firestore with caching (defined early for hooks that depend on it)
  const loadLogs = useCallback(async (loadMore = false) => {
    console.log('🔄 Starting loadLogs...', { loadMore, filters });
    
    try {
      if (!loadMore) {
        setLoading(true);
        setError(null);
        console.log('📊 Set loading to true');
      }

      // Check cache first (only for initial load)
      const cacheKey = logCache.generateKey(filters, currentPage, logsPerPage);
      if (!loadMore && !isRealTime) {
        const cachedLogs = logCache.get(cacheKey);
        if (cachedLogs) {
          console.log('📦 Using cached logs:', cachedLogs.length);
          setLogs(cachedLogs);
          setLoading(false);
          setLastUpdate(new Date());
          return;
        }
      }

      // Build query
      let q = query(
        collection(db, 'audit_logs'),
        orderBy('timestamp', 'desc'),
        limit(logsPerPage)
      );

      // Apply role-based access control
      if (!hasPermission(permissions, PERMISSIONS.VIEW_ALL_LOGS)) {
        if (hasPermission(permissions, PERMISSIONS.VIEW_TEAM_LOGS)) {
          // Manager: can view team logs (implementation would need team info)
          // For now, filter by user domain or team identifier
          // This would need to be enhanced based on your team structure
          console.log('Applying team-based log filtering for manager role');
        } else if (hasPermission(permissions, PERMISSIONS.VIEW_OWN_LOGS)) {
          // User: can only view their own logs
          const currentUser = auth.currentUser;
          if (currentUser) {
            q = query(q, where('userId', '==', currentUser.uid));
          }
        } else {
          // Viewer or no permissions: no logs accessible
          setLogs([]);
          setLoading(false);
          setError('You do not have permission to view audit logs.');
          return;
        }
      }

      // Add filters
      console.log('🔧 Building query with filters:', filters);
      
      if (filters.action && filters.action !== '' && filters.action !== 'all') {
        console.log('🎯 Adding action filter:', filters.action);
        q = query(q, where('action', '==', filters.action));
      }
      // Note: We'll filter system logs client-side to avoid complex composite index requirements
      
      if (filters.resource && filters.resource !== 'all') {
        console.log('📁 Adding resource filter:', filters.resource);
        q = query(q, where('resource', '==', filters.resource));
      }
      if (filters.severity && filters.severity !== 'all') {
        console.log('⚠️ Adding severity filter:', filters.severity);
        q = query(q, where('severity', '==', filters.severity));
      }

      // Execute query with timeout
      console.log('🔍 Executing Firestore query...');
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Query timeout - taking too long')), 10000)
      );
      
      const queryPromise = getDocs(q);
      const querySnapshot = await Promise.race([queryPromise, timeoutPromise]);
      
      console.log('✅ Query completed. Documents found:', querySnapshot.docs.length);
      
      const newLogs = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // If no logs found with the current query, try a simple fallback to check if ANY logs exist
      if (newLogs.length === 0) {
        console.log('🔍 No logs found with current query, trying fallback query...');
        try {
          const fallbackQuery = query(
            collection(db, 'audit_logs'),
            orderBy('timestamp', 'desc'),
            limit(10)
          );
          const fallbackSnapshot = await getDocs(fallbackQuery);
          
          if (fallbackSnapshot.docs.length > 0) {
            console.log('📝 Found logs with fallback query:', fallbackSnapshot.docs.length);
            
            // Check if there are non-system logs
            const nonSystemLogs = fallbackSnapshot.docs.filter(doc => {
              const data = doc.data();
              const systemActions = ['REALTIME_SYNC', 'TEST', 'MANUAL_TEST', 'SYSTEM'];
              return !systemActions.includes(data.action);
            });
            
            if (nonSystemLogs.length === 0) {
              setError(`No operational logs found. There are ${fallbackSnapshot.docs.length} system logs available. Try selecting "All Actions (Including System)" to see them.`);
            } else {
              setError(`No logs match your current filters. Try adjusting your search criteria.`);
            }
          } else {
            console.log('📭 No logs found at all');
            setError('No logs found. Start using the application to generate audit logs.');
          }
        } catch (fallbackErr) {
          console.warn('Fallback query failed:', fallbackErr);
        }
      }

      // Track read operations for cost monitoring
      AuditLogger.trackRead(querySnapshot.docs.length);

      if (loadMore) {
        setLogs(prev => [...prev, ...newLogs]);
        console.log('📈 Added more logs. Total:', logs.length + newLogs.length);
      } else {
        setLogs(newLogs);
        console.log('🔄 Set new logs. Count:', newLogs.length);
        // Cache the results (only if not real-time)
        if (!isRealTime) {
          logCache.set(cacheKey, newLogs);
        }
      }

      setLastUpdate(new Date());
      setLastFetchTime(new Date());
      console.log('✅ loadLogs completed successfully');

    } catch (err) {
      console.error('❌ Error loading logs:', err);
      setError(`Failed to load logs: ${err.message}`);
      
      // If no logs found due to filters, show helpful message
      if (err.message.includes('timeout') || err.message.includes('Query timeout')) {
        setError('Query is taking too long. Try simplifying your filters or check your internet connection.');
      }
    } finally {
      if (!loadMore) {
        setLoading(false);
        console.log('📊 Set loading to false');
      }
    }
  }, [filters, currentPage, logsPerPage, isRealTime, permissions, logs.length]);

  // Keyboard shortcuts setup
  const keyboardShortcuts = createLogsKeyboardShortcuts({
    onRefresh: () => loadLogs(),
    onExport: () => setShowExporter(true),
    onClearFilters: clearFilters,
    onToggleRealTime: () => setIsRealTime(!isRealTime),
    onFocusSearch: () => searchInputRef.current?.focus()
  });

  useKeyboardShortcuts(keyboardShortcuts);

  // GDPR consent check
  useEffect(() => {
    const currentUser = auth.currentUser;
    if (currentUser && !GDPR.hasConsent(currentUser.uid, 'audit_logs')) {
      setShowConsentManager(true);
    }
  }, []);

  // Load logs on mount and when key dependencies change
  useEffect(() => {
    console.log('🚀 Loading logs due to dependency change...', { 
      filters, 
      currentPage, 
      isRealTime,
      hasPermissions: !!permissions,
      userRole,
      hasLoadLogs: typeof loadLogs === 'function'
    });
    
    // Only load if permissions are available and loadLogs function is defined
    if (permissions && typeof loadLogs === 'function') {
      loadLogs();
    } else {
      console.log('⏳ Waiting for permissions and loadLogs function...');
    }
  }, [filters, currentPage, logsPerPage, isRealTime, permissions, userRole, loadLogs]);

  // Safety timeout to prevent indefinite loading
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading) {
        console.warn('⚠️ Loading timeout reached, resetting loading state');
        setLoading(false);
        setError('Loading took too long. Please try refreshing the page.');
      }
    }, 15000); // 15 second timeout

    return () => clearTimeout(timeout);
  }, [loading]);

  // Auto-refresh effect
  useEffect(() => {
    if (!autoRefresh || isRealTime) return;

    const interval = setInterval(() => {
      console.log('🔄 Auto-refresh triggered...');
      // Call loadLogs directly without dependency to avoid circular dependency
      if (permissions) {
        loadLogs();
      }
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, isRealTime, permissions]); // Removed loadLogs dependency

  // Decide whether to use virtual scrolling based on expansion state
  const shouldUseVirtualScrolling = !expandedLogId && filteredLogs.length > 50;

  // Virtual scrolling for large datasets (only when no logs are expanded)
  const getItemHeight = useCallback((item, index) => {
    const baseHeight = viewMode === 'detailed' ? 120 : 80;
    return baseHeight;
  }, [viewMode]);

  const virtualScrollData = useVirtualScroll({
    items: shouldUseVirtualScrolling ? filteredLogs : [],
    itemHeight: getItemHeight, 
    containerHeight: 600,
    overscan: 5
  });

  // Use virtual scrolling data only when appropriate
  const {
    scrollElementRef,
    visibleItems: virtualVisibleLogs,
    totalHeight: virtualTotalHeight,
    offsetY: virtualOffsetY,
    handleScroll,
    scrollToTop
  } = virtualScrollData;

  // Select which logs to render and how
  const logsToRender = shouldUseVirtualScrolling ? virtualVisibleLogs : filteredLogs;
  const totalHeight = shouldUseVirtualScrolling ? virtualTotalHeight : 'auto';
  const offsetY = shouldUseVirtualScrolling ? virtualOffsetY : 0;

  // Save filters to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('auditLogFilters', JSON.stringify(filters));
  }, [filters]);

  // Set up real-time listener
  useEffect(() => {
    if (!isRealTime) return;

    const q = query(
      collection(db, 'audit_logs'),
      orderBy('timestamp', 'desc'),
      limit(logsPerPage)
    );

    const unsubscribe = onSnapshot(q, 
      (querySnapshot) => {
        const newLogs = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setLogs(newLogs);
        setLastUpdate(new Date());
        setError(null);
      },
      (err) => {
        console.error('Real-time listener error:', err);
        setError(`Real-time updates failed: ${err.message}`);
      }
    );

    return () => unsubscribe();
  }, [isRealTime, logsPerPage]);

  // Filter and search logs
  useEffect(() => {
    let filtered = logs;

    // Apply search
    if (debouncedSearchTerm) {
      const searchLower = debouncedSearchTerm.toLowerCase();
      filtered = filtered.filter(log => {
        const formatted = formatLogEntry(log);
        
        // Search in all relevant fields
        const searchableFields = [
          // Formatted data
          formatted.summary,
          formatted.user.email,
          formatted.user.name,
          formatted.action.label,
          formatted.resource.label,
          
          // Raw log data
          log.userEmail,
          log.userId,
          log.action,
          log.resource,
          log.resourceId,
          log.description,
          log.entityId,
          
          // Changes data (if present)
          log.changes?.after?.clientName,
          log.changes?.after?.clientEmail,
          log.changes?.after?.emailEdu,
          log.changes?.after?.productName,
          log.changes?.after?.transactionId,
          log.changes?.after?.status,
          log.changes?.after?.paymentMethod,
          log.changes?.before?.clientName,
          log.changes?.before?.clientEmail,
          log.changes?.before?.emailEdu,
          
          // Metadata fields
          log.metadata?.source,
          log.metadata?.sessionId,
          
          // Tags (if present)
          ...(log.tags || [])
        ].filter(Boolean); // Remove undefined/null values
        
        // Check if search term matches any field
        return searchableFields.some(field => 
          field && field.toString().toLowerCase().includes(searchLower)
        );
      });
    }

    // Apply system log filtering (exclude by default unless explicitly requested)
    if (!filters.action || filters.action === '') {
      // By default, exclude system logs
      const systemActions = ['REALTIME_SYNC', 'TEST', 'MANUAL_TEST', 'SYSTEM'];
      filtered = filtered.filter(log => !systemActions.includes(log.action));
      console.log('🚫 Client-side filtering: Excluded system logs');
    } else if (filters.action === 'all') {
      // Show all logs including system logs when explicitly requested
      console.log('📝 Client-side filtering: Showing all logs including system');
    }

    // Apply action filter
    if (filters.action && filters.action !== '' && filters.action !== 'all') {
      filtered = filtered.filter(log => log.action === filters.action);
    }

    // Apply entity type filter
    if (filters.entityType) {
      filtered = filtered.filter(log => log.entityType === filters.entityType);
    }

    // Apply user filter
    if (filters.userId) {
      filtered = filtered.filter(log => log.userId === filters.userId);
    }

    // Apply severity filter
    if (filters.severity) {
      filtered = filtered.filter(log => log.severity === filters.severity);
    }

    // Apply date range filter
    if (filters.dateFrom || filters.dateTo) {
      filtered = filtered.filter(log => {
        const logDate = log.timestamp?.toDate ? log.timestamp.toDate() : new Date(log.timestamp);
        
        if (filters.dateFrom) {
          const startDate = new Date(filters.dateFrom);
          if (logDate < startDate) return false;
        }
        
        if (filters.dateTo) {
          const endDate = new Date(filters.dateTo);
          endDate.setHours(23, 59, 59, 999); // Include full end date
          if (logDate > endDate) return false;
        }
        
        return true;
      });
    }

    setFilteredLogs(filtered);
    setCurrentPage(1); // Reset to first page when filtering
  }, [logs, debouncedSearchTerm, filters]);

  // Get current page logs
  // eslint-disable-next-line no-unused-vars
  const getCurrentPageLogs = useCallback(() => {
    const startIndex = (currentPage - 1) * logsPerPage;
    const endIndex = startIndex + logsPerPage;
    return filteredLogs.slice(startIndex, endIndex);
  }, [filteredLogs, currentPage, logsPerPage]);

  // Handle filter changes
  const handleFilterChange = useCallback((filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  }, []);

  // Cleanup system/test logs
  const cleanupSystemLogs = useCallback(async () => {
    if (!hasPermission(permissions, PERMISSIONS.VIEW_ALL_LOGS)) {
      setError('You do not have permission to cleanup logs.');
      return;
    }

    // First, count the logs to give user better information
    try {
      const countQuery = query(
        collection(db, 'audit_logs'),
        where('action', 'in', ['REALTIME_SYNC', 'TEST', 'MANUAL_TEST'])
      );
      const countSnapshot = await getDocs(countQuery);
      const logCount = countSnapshot.docs.length;
      
      const confirmed = window.confirm(
        `This will remove ${logCount} system/test logs (REALTIME_SYNC, TEST, MANUAL_TEST). ` +
        `This may take a few moments. Are you sure?`
      );
      
      if (!confirmed) return;

      setCleanupLoading(true);
      setError(null);
      
      console.log(`Found ${logCount} system/test logs to delete`);
      
      if (logCount === 0) {
        setSuccess('No system/test logs found to cleanup');
        setCleanupLoading(false);
        return;
      }

      // Process in batches to avoid overwhelming the system
      const batchSize = 500; // Firebase batch limit
      let deletedCount = 0;
      
      for (let i = 0; i < countSnapshot.docs.length; i += batchSize) {
        const batchDocs = countSnapshot.docs.slice(i, i + batchSize);
        const deletePromises = batchDocs.map(doc => deleteDoc(doc.ref));
        
        await Promise.all(deletePromises);
        deletedCount += batchDocs.length;
        
        // Update progress
        console.log(`Deleted ${deletedCount}/${logCount} logs...`);
        
        // Small delay to prevent overwhelming Firebase
        if (i + batchSize < countSnapshot.docs.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      // Log the cleanup action
      await AuditLogger.logUserAction('CLEANUP', {
        operation: 'system_logs_cleanup',
        deletedCount: deletedCount,
        deletedTypes: ['REALTIME_SYNC', 'TEST', 'MANUAL_TEST']
      });
      
      setSuccess(`Successfully cleaned up ${deletedCount} system/test logs`);
      
      // Refresh logs after cleanup
      await loadLogs();
      
    } catch (error) {
      console.error('Error cleaning up logs:', error);
      setError(`Failed to cleanup logs: ${error.message}`);
      
      // Log the error
      await AuditLogger.logSystemEvent('CLEANUP_ERROR', {
        operation: 'system_logs_cleanup',
        error: error.message
      });
    } finally {
      setCleanupLoading(false);
    }
  }, [permissions, loadLogs]);

  // Create demo audit logs
  const createDemoLogs = useCallback(async () => {
    if (!hasPermission(permissions, PERMISSIONS.VIEW_ALL_LOGS)) {
      setError('You do not have permission to create demo logs.');
      return;
    }

    setDemoLoading(true);
    setError(null);
    
    try {
      const timestamp = Date.now().toString().slice(-4);
      
      // Create a demo sale transaction
      const demoSale = {
        transactionId: `DEMO-${timestamp}`,
        clientName: `Demo Client ${timestamp}`,
        productName: 'Demo Product License',
        price: '299',
        amount: '1',
        emailEdu: `demo${timestamp}@university.edu`,
        clientEmail: `demo${timestamp}@gmail.com`,
        paymentMethod: 'Demo Payment',
        contact1: '+1-555-DEMO',
        contact2: '@demo_user',
        date: new Date().toISOString().split('T')[0],
        status: 'Completed',
        subscriptionPeriod: '12 months',
        notes: 'Demo sale created from Audit Logs page for testing',
        createdAt: new Date()
      };

      // Store the demo sale in Sales collection
      await setDoc(doc(db, 'Sales', demoSale.transactionId), demoSale);

      // Log the creation
      await AuditLogger.logSalesChange(
        'CREATE',
        demoSale.transactionId,
        {}, // no before data for new sales
        demoSale,
        {
          source: 'logs_page_demo',
          isDemoData: true,
          createdBy: 'logs_demo_button'
        }
      );

      // Create a demo edit log
      const updatedSale = { ...demoSale, notes: 'Updated demo sale for audit testing' };
      await AuditLogger.logSalesChange(
        'UPDATE',
        demoSale.transactionId,
        demoSale,
        updatedSale,
        {
          source: 'logs_page_demo',
          isDemoData: true,
          action: 'demo_edit'
        }
      );

      setSuccess(`Created demo logs for transaction: ${demoSale.transactionId}`);
      
      // Refresh logs to show new entries
      await loadLogs();
      
    } catch (error) {
      console.error('Error creating demo logs:', error);
      setError(`Failed to create demo logs: ${error.message}`);
    } finally {
      setDemoLoading(false);
    }
  }, [permissions, loadLogs]);

  // Auto-clear success messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  if (loading && logs.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="h-8 w-8 text-blue-600" />
              <h1 className="text-3xl font-bold text-gray-900">Audit Logs</h1>
            </div>
            <p className="text-gray-600">System activity and change tracking</p>
          </div>

          {/* Loading skeleton */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="space-y-4">
              {[...Array(10)].map((_, i) => (
                <div key={i} className="flex items-center space-x-4">
                  <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4"></div>
                    <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2"></div>
                  </div>
                  <div className="w-20 h-4 bg-gray-200 rounded animate-pulse"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-200 ${themeStyles.bg.secondary}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <FileText className={`h-8 w-8 ${themeStyles.text.accent}`} />
                <h1 className={`text-3xl font-bold ${themeStyles.text.primary}`}>Audit Logs</h1>
              </div>
              <p className={themeStyles.text.secondary}>
                System activity and change tracking • {filteredLogs.length} logs
                {userRole && (
                  <span className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${
                    userRole === 'admin' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                    userRole === 'manager' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                    userRole === 'user' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                    'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                  }`}>
                    {userRole.charAt(0).toUpperCase() + userRole.slice(1)}
                  </span>
                )}
                {lastUpdate && (
                  <span className={`text-sm ${themeStyles.text.muted} ml-2`}>
                    Last updated: {lastUpdate.toLocaleTimeString()}
                  </span>
                )}
                {lastFetchTime && (
                  <span className={`text-xs ${themeStyles.text.muted} ml-2`}>
                    • Fetched: {lastFetchTime.toLocaleTimeString()}
                  </span>
                )}
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Dark mode toggle */}
              <DarkModeToggle variant="icon" showLabel={false} />
              
              {/* Performance indicators */}
              <div className={`flex items-center gap-2 text-xs ${themeStyles.text.muted}`}>
                {!isRealTime && logCache.getStats().size > 0 && (
                  <div className="flex items-center gap-1">
                    <Zap className="w-3 h-3" />
                    <span>Cached</span>
                  </div>
                )}
                {autoRefresh && !isRealTime && (
                  <div className="flex items-center gap-1">
                    <RefreshCw className="w-3 h-3 animate-pulse" />
                    <span>Auto-refresh</span>
                  </div>
                )}
              </div>

              {/* Keyboard help */}
              <button
                onClick={() => setShowKeyboardHelp(true)}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                title="Keyboard shortcuts"
              >
                <Keyboard className="w-4 h-4" />
              </button>

              {/* Auto-refresh toggle */}
              {!isRealTime && (
                <button
                  onClick={() => setAutoRefresh(!autoRefresh)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    autoRefresh 
                      ? 'bg-orange-100 text-orange-800 hover:bg-orange-200' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <RefreshCw className={`w-4 h-4 ${autoRefresh ? 'animate-pulse' : ''}`} />
                  Auto-refresh
                </button>
              )}

              {/* View mode toggle */}
              <div className="flex items-center gap-1 bg-gray-100 rounded-md p-1">
                <button
                  onClick={() => setViewMode('compact')}
                  className={`px-3 py-1 text-sm rounded-md transition-colors ${
                    viewMode === 'compact' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Compact
                </button>
                <button
                  onClick={() => setViewMode('detailed')}
                  className={`px-3 py-1 text-sm rounded-md transition-colors ${
                    viewMode === 'detailed' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Eye className="h-3 w-3 mr-1 inline" />
                  Detailed
                </button>
              </div>

              {/* Real-time toggle */}
              <button
                onClick={() => setIsRealTime(!isRealTime)}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isRealTime 
                    ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <div className={`w-2 h-2 rounded-full ${isRealTime ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                {isRealTime ? 'Live' : 'Static'}
              </button>

              {/* Manual refresh */}
              <button
                onClick={() => loadLogs()}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>

              {/* Security dashboard button */}
              {hasPermission(permissions, PERMISSIONS.VIEW_ALL_LOGS) && (
                <button
                  onClick={() => setShowSecurityDashboard(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                >
                  <Shield className="h-4 w-4" />
                  Security
                </button>
              )}

              {/* Security alerts button */}
              {hasPermission(permissions, PERMISSIONS.VIEW_ALL_LOGS) && (
                <button
                  onClick={() => setShowSecurityAlerts(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  <AlertCircle className="h-4 w-4" />
                  Alerts
                </button>
              )}

              {/* Export button */}
              {hasPermission(permissions, PERMISSIONS.EXPORT_LOGS) && (
                <button
                  onClick={() => setShowExporter(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                >
                  <Download className="h-4 w-4" />
                  Export
                </button>
              )}

              {/* Cleanup button */}
              {hasPermission(permissions, PERMISSIONS.VIEW_ALL_LOGS) && (
                <button
                  onClick={cleanupSystemLogs}
                  disabled={cleanupLoading}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  <Trash2 className={`h-4 w-4 ${cleanupLoading ? 'animate-spin' : ''}`} />
                  Cleanup System Logs
                </button>
              )}

              {/* Demo button */}
              {hasPermission(permissions, PERMISSIONS.VIEW_ALL_LOGS) && (
                <button
                  onClick={createDemoLogs}
                  disabled={demoLoading}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  <PlayCircle className={`h-4 w-4 ${demoLoading ? 'animate-spin' : ''}`} />
                  Create Demo Logs
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Filters */}
        <LogFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={clearFilters}
          loading={loading}
        />

        {/* Performance Metrics */}
        <PerformanceMetrics logs={logs} />

        {/* Search */}
        <div className={`${themeStyles.bg.primary} rounded-lg shadow-sm p-4 mb-6`}>
          <div className="relative">
            <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${themeStyles.text.muted} h-4 w-4`} />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search logs by user, email, action, client name, transaction ID, or any data..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${themeStyles.input.primary} ${themeStyles.border.primary}`}
            />
            {isSearching && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              </div>
            )}
          </div>
        </div>

        {/* Error display */}
        {error && (
          <div className={`${themeStyles.status.error} border rounded-md p-4 mb-6`}>
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              <p>{error}</p>
            </div>
          </div>
        )}

        {/* Success display */}
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-800 rounded-md p-4 mb-6">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5" />
              <p>{success}</p>
            </div>
          </div>
        )}

        {/* Logs display */}
        <div className="space-y-4">
          {filteredLogs.length === 0 ? (
            <div className={`${themeStyles.bg.primary} rounded-lg p-12 text-center`}>
              <FileText className={`h-12 w-12 ${themeStyles.text.muted} mx-auto mb-4`} />
              <h3 className={`text-lg font-medium ${themeStyles.text.primary} mb-2`}>No logs found</h3>
              <p className={themeStyles.text.secondary}>
                {debouncedSearchTerm || Object.values(filters).some(v => v)
                  ? 'Try adjusting your search or filters'
                  : 'No audit logs have been recorded yet'
                }
              </p>
            </div>
          ) : (
            <>
              {/* Virtual or normal scrolling container */}
              <div className={`${themeStyles.bg.primary} rounded-lg`}>
                <div 
                  ref={scrollElementRef}
                  className="overflow-auto"
                  style={{ height: '600px' }}
                  onScroll={shouldUseVirtualScrolling ? handleScroll : undefined}
                >
                  {shouldUseVirtualScrolling ? (
                    // Virtual scrolling mode (no expanded logs)
                    <div style={{ height: totalHeight, position: 'relative' }}>
                      <div
                        style={{
                          transform: `translateY(${offsetY}px)`,
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                        }}
                      >
                        {logsToRender.map((log, index) => {
                          const itemHeight = getItemHeight(log, index);
                          
                          return (
                            <div
                              key={log.id}
                              style={{
                                height: `${itemHeight}px`,
                                paddingBottom: '8px'
                              }}
                            >
                              {viewMode === 'detailed' ? (
                                <LogViewer
                                  log={log}
                                  expanded={false} // Never expanded in virtual scrolling mode
                                  onToggleExpanded={() => setExpandedLogId(log.id)}
                                />
                              ) : (
                                <LogEntry 
                                  log={log}
                                  expanded={false} // Never expanded in virtual scrolling mode
                                  onToggleExpanded={() => setExpandedLogId(log.id)}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    // Normal scrolling mode (with expanded logs support)
                    <div className="space-y-2 p-4">
                      {logsToRender.map((log) => {
                        const isExpanded = expandedLogId === log.id;
                        
                        return (
                          <div
                            key={log.id}
                            className={`transition-all duration-200 ${
                              isExpanded ? 'bg-blue-50 border border-blue-200 rounded-lg' : ''
                            }`}
                          >
                            {viewMode === 'detailed' ? (
                              <LogViewer
                                log={log}
                                expanded={isExpanded}
                                onToggleExpanded={() => setExpandedLogId(
                                  expandedLogId === log.id ? null : log.id
                                )}
                              />
                            ) : (
                              <LogEntry 
                                log={log}
                                expanded={isExpanded}
                                onToggleExpanded={() => setExpandedLogId(
                                  expandedLogId === log.id ? null : log.id
                                )}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                
                {/* Scroll controls */}
                <div className={`${themeStyles.border.primary} border-t px-4 py-2 flex justify-between items-center`}>
                  <div className={`text-sm ${themeStyles.text.secondary}`}>
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        Loading logs...
                      </span>
                    ) : (
                      <span>
                        {filteredLogs.length} logs total
                        {shouldUseVirtualScrolling && (
                          <span className="ml-2 text-xs text-blue-600">• Virtual scrolling</span>
                        )}
                        {expandedLogId && (
                          <span className="ml-2 text-xs text-orange-600">• Expanded view</span>
                        )}
                      </span>
                    )}
                  </div>
                  
                  <button
                    onClick={scrollToTop}
                    className={`flex items-center gap-1 px-3 py-1 text-sm rounded-md transition-colors ${themeStyles.button.secondary}`}
                  >
                    <Zap className="h-3 w-3" />
                    Scroll to Top
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Log Exporter Modal */}
      <LogExporter
        logs={logs}
        filters={filters}
        isVisible={showExporter}
        onClose={() => setShowExporter(false)}
      />

      {/* Keyboard Shortcuts Help Modal */}
      {showKeyboardHelp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Keyboard className="w-5 h-5 mr-2" />
                Keyboard Shortcuts
              </h3>
              <button
                onClick={() => setShowKeyboardHelp(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-3 text-sm">
              {keyboardShortcuts.map((shortcut, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-gray-700">{shortcut.description}</span>
                  <div className="flex items-center gap-1">
                    {shortcut.ctrl && (
                      <kbd className="px-2 py-1 bg-gray-100 border border-gray-300 rounded text-xs">
                        Ctrl
                      </kbd>
                    )}
                    {shortcut.shift && (
                      <kbd className="px-2 py-1 bg-gray-100 border border-gray-300 rounded text-xs">
                        Shift
                      </kbd>
                    )}
                    {shortcut.alt && (
                      <kbd className="px-2 py-1 bg-gray-100 border border-gray-300 rounded text-xs">
                        Alt
                      </kbd>
                    )}
                    <kbd className="px-2 py-1 bg-gray-100 border border-gray-300 rounded text-xs">
                      {shortcut.key === ' ' ? 'Space' : shortcut.key}
                    </kbd>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowKeyboardHelp(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Security Dashboard */}
      <SecurityDashboard
        logs={logs}
        isVisible={showSecurityDashboard}
        onClose={() => setShowSecurityDashboard(false)}
      />

      {/* Security Alerts */}
      <SecurityAlerts
        logs={logs}
        isVisible={showSecurityAlerts}
        onClose={() => setShowSecurityAlerts(false)}
      />

      {/* GDPR Consent Manager */}
      <ConsentManager
        userId={auth.currentUser?.uid}
        isVisible={showConsentManager}
        onClose={() => setShowConsentManager(false)}
        onConsentChange={(consents) => {
          if (consents.audit_logs) {
            setShowConsentManager(false);
            loadLogs(); // Reload logs after consent is granted
          }
        }}
        requiredForAccess={true}
      />

      {/* Firebase Cost Monitor */}
      <CostMonitor isDarkMode={isDarkMode} />
    </div>
  );
};

export default LogsPage;
